import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload,
  FileVideo,
  X,
  Check,
  ChevronRight,
  ChevronLeft,
  FileText,
  Bot,
  Globe,
  BookOpen,
  GraduationCap,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { uploads as uploadsApi, type OutputType } from '../../lib/api'
import { useJobsStore } from '../../stores/jobs'

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_SIZE_BYTES = 4 * 1024 * 1024 * 1024 // 4 GB
const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo']

const OUTPUT_OPTIONS: Array<{
  type: OutputType
  label: string
  description: string
  icon: React.ReactNode
}> = [
  {
    type: 'sop',
    label: 'SOP Document',
    description: 'Step-by-step standard operating procedure with visuals',
    icon: <FileText size={22} />,
  },
  {
    type: 'agent_prompt',
    label: 'Agent Prompt',
    description: 'AI agent system prompt trained on the procedure',
    icon: <Bot size={22} />,
  },
  {
    type: 'website',
    label: 'Web Page',
    description: 'Responsive HTML page with embedded procedure',
    icon: <Globe size={22} />,
  },
  {
    type: 'blog',
    label: 'Blog Post',
    description: 'Long-form article suitable for internal knowledge base',
    icon: <BookOpen size={22} />,
  },
  {
    type: 'course_module',
    label: 'Course Module',
    description: 'E-learning module with quiz questions',
    icon: <GraduationCap size={22} />,
  },
]

const DIVISIONS = ['Corporate', 'Leak Seal', 'Geotech', 'SRN']

const STEPS = ['Select File', 'Outputs', 'Details', 'Confirm']

// ─── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({ current }: { current: number }) {
  return (
    <div className="flex items-center mb-8">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-7 h-7 flex items-center justify-center text-xs font-display font-bold border-2 ${
                i < current
                  ? 'bg-alk-red border-alk-red text-white'
                  : i === current
                  ? 'bg-alk-black border-alk-black text-white'
                  : 'bg-white border-gray-300 text-alk-gray'
              }`}
            >
              {i < current ? <Check size={13} /> : i + 1}
            </div>
            <span
              className={`text-xs mt-1 font-display uppercase tracking-wide ${
                i === current ? 'text-alk-black' : 'text-alk-gray'
              }`}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`h-0.5 flex-1 mx-2 mb-4 ${
                i < current ? 'bg-alk-red' : 'bg-gray-200'
              }`}
              style={{ minWidth: '2rem' }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Step 1: File Selection ───────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function FileStep({
  file,
  onFile,
  error,
}: {
  file: File | null
  onFile: (f: File | null) => void
  error: string | null
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const validate = (f: File): string | null => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      return 'Unsupported file type. Please upload MP4, MOV, WebM, or AVI.'
    }
    if (f.size > MAX_SIZE_BYTES) {
      return 'File exceeds 4 GB limit.'
    }
    return null
  }

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return
      const f = files[0]
      const err = validate(f)
      if (err) {
        onFile(null)
        return
      }
      onFile(f)
    },
    [onFile]
  )

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <div>
      <h2 className="font-display text-xl uppercase tracking-wide text-alk-black mb-1">
        Select Video File
      </h2>
      <p className="text-sm text-alk-gray mb-5">
        MP4, MOV, WebM, AVI up to 4 GB.
      </p>

      {!file ? (
        <div
          className={`border-2 border-dashed p-12 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            dragging
              ? 'border-alk-red bg-red-50'
              : 'border-gray-300 hover:border-alk-red hover:bg-gray-50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          <Upload size={32} className="text-alk-gray mb-3" />
          <p className="font-display text-lg uppercase tracking-wide text-alk-black mb-1">
            Drag & drop or click to browse
          </p>
          <p className="text-xs text-alk-gray">MP4, MOV, WebM, AVI — max 4 GB</p>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      ) : (
        <div className="card flex items-center gap-4">
          <div className="w-10 h-10 bg-alk-black flex items-center justify-center flex-shrink-0">
            <FileVideo size={18} className="text-alk-red" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-alk-black truncate">{file.name}</div>
            <div className="text-xs text-alk-gray mt-0.5">{formatBytes(file.size)}</div>
          </div>
          <button
            onClick={() => onFile(null)}
            className="text-alk-gray hover:text-alk-red transition-colors"
            aria-label="Remove file"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 mt-3 p-2.5 bg-red-50 border border-red-200">
          <AlertCircle size={14} className="text-alk-red flex-shrink-0" />
          <span className="text-xs text-alk-red">{error}</span>
        </div>
      )}
    </div>
  )
}

// ─── Step 2: Outputs ──────────────────────────────────────────────────────────

function OutputsStep({
  selected,
  onChange,
}: {
  selected: OutputType[]
  onChange: (v: OutputType[]) => void
}) {
  const toggle = (type: OutputType) => {
    onChange(
      selected.includes(type)
        ? selected.filter((t) => t !== type)
        : [...selected, type]
    )
  }

  return (
    <div>
      <h2 className="font-display text-xl uppercase tracking-wide text-alk-black mb-1">
        Select Output Types
      </h2>
      <p className="text-sm text-alk-gray mb-5">
        Choose what you want generated from this video.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {OUTPUT_OPTIONS.map((opt) => {
          const isSelected = selected.includes(opt.type)
          return (
            <button
              key={opt.type}
              type="button"
              onClick={() => toggle(opt.type)}
              className={`text-left p-4 border-2 transition-colors ${
                isSelected
                  ? 'border-alk-red bg-red-50'
                  : 'border-gray-200 bg-white hover:border-gray-400'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-alk-red text-white' : 'bg-gray-100 text-alk-gray'
                  }`}
                >
                  {opt.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-display text-sm uppercase tracking-wide font-semibold ${
                        isSelected ? 'text-alk-red' : 'text-alk-black'
                      }`}
                    >
                      {opt.label}
                    </span>
                    {isSelected && (
                      <Check size={14} className="text-alk-red flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-alk-gray mt-0.5">{opt.description}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-alk-red mt-3 flex items-center gap-1">
          <AlertCircle size={12} /> Select at least one output type.
        </p>
      )}
    </div>
  )
}

// ─── Step 3: Details ──────────────────────────────────────────────────────────

interface Metadata {
  title: string
  description: string
  division: string
}

function DetailsStep({
  meta,
  onChange,
}: {
  meta: Metadata
  onChange: (m: Metadata) => void
}) {
  return (
    <div>
      <h2 className="font-display text-xl uppercase tracking-wide text-alk-black mb-1">
        Optional Details
      </h2>
      <p className="text-sm text-alk-gray mb-5">
        Add metadata to help organize this job.
      </p>

      <div className="space-y-4">
        <div>
          <label className="section-label block mb-1.5">
            Title <span className="text-alk-gray normal-case font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={meta.title}
            onChange={(e) => onChange({ ...meta, title: e.target.value })}
            placeholder="e.g. Pipe Repair Procedure"
            className="input-field"
          />
        </div>

        <div>
          <label className="section-label block mb-1.5">
            Description <span className="text-alk-gray normal-case font-normal">(optional)</span>
          </label>
          <textarea
            value={meta.description}
            onChange={(e) => onChange({ ...meta, description: e.target.value })}
            placeholder="Brief description of the procedure..."
            rows={3}
            className="input-field resize-none"
          />
        </div>

        <div>
          <label className="section-label block mb-1.5">Division</label>
          <select
            value={meta.division}
            onChange={(e) => onChange({ ...meta, division: e.target.value })}
            className="input-field"
          >
            <option value="">Select division...</option>
            {DIVISIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

// ─── Step 4: Confirm ──────────────────────────────────────────────────────────

function ConfirmStep({
  file,
  outputs,
  meta,
  uploadProgress,
  isUploading,
  uploadError,
}: {
  file: File
  outputs: OutputType[]
  meta: Metadata
  uploadProgress: number
  isUploading: boolean
  uploadError: string | null
}) {
  return (
    <div>
      <h2 className="font-display text-xl uppercase tracking-wide text-alk-black mb-1">
        Review & Submit
      </h2>
      <p className="text-sm text-alk-gray mb-5">
        Confirm your selections before starting the job.
      </p>

      <div className="space-y-4">
        {/* File */}
        <div className="card">
          <div className="section-label mb-2">File</div>
          <div className="flex items-center gap-2">
            <FileVideo size={15} className="text-alk-gray" />
            <span className="text-sm font-medium text-alk-black">{file.name}</span>
            <span className="text-xs text-alk-gray">({formatBytes(file.size)})</span>
          </div>
        </div>

        {/* Outputs */}
        <div className="card">
          <div className="section-label mb-2">Outputs ({outputs.length})</div>
          <div className="flex flex-wrap gap-2">
            {outputs.map((o) => {
              const opt = OUTPUT_OPTIONS.find((x) => x.type === o)
              return (
                <span key={o} className="badge bg-alk-black text-white">
                  {opt?.label ?? o}
                </span>
              )
            })}
          </div>
        </div>

        {/* Metadata */}
        {(meta.title || meta.description || meta.division) && (
          <div className="card">
            <div className="section-label mb-2">Details</div>
            {meta.title && (
              <div className="text-sm text-alk-black mb-1">
                <span className="text-alk-gray">Title: </span>
                {meta.title}
              </div>
            )}
            {meta.description && (
              <div className="text-sm text-alk-black mb-1">
                <span className="text-alk-gray">Description: </span>
                {meta.description}
              </div>
            )}
            {meta.division && (
              <div className="text-sm text-alk-black">
                <span className="text-alk-gray">Division: </span>
                {meta.division}
              </div>
            )}
          </div>
        )}

        {/* Upload progress */}
        {isUploading && (
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 size={14} className="animate-spin text-alk-red" />
              <span className="section-label">
                {uploadProgress < 100 ? 'Uploading...' : 'Creating job...'}
              </span>
            </div>
            <div className="bg-gray-200 h-1.5">
              <div
                className="bg-alk-red h-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="text-xs text-alk-gray mt-1">{uploadProgress}%</div>
          </div>
        )}

        {uploadError && (
          <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200">
            <AlertCircle size={14} className="text-alk-red flex-shrink-0" />
            <span className="text-xs text-alk-red">{uploadError}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function UploadPage() {
  const navigate = useNavigate()
  const { createJob } = useJobsStore()

  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [selectedOutputs, setSelectedOutputs] = useState<OutputType[]>(['sop'])
  const [meta, setMeta] = useState<Metadata>({ title: '', description: '', division: '' })
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleFileChange = (f: File | null) => {
    setFile(f)
    setFileError(f === null && file !== null ? 'Please select a valid video file.' : null)
  }

  const canAdvance = () => {
    if (step === 0) return file !== null
    if (step === 1) return selectedOutputs.length > 0
    return true
  }

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1)
  }

  const handleSubmit = async () => {
    if (!file) return
    setIsUploading(true)
    setUploadError(null)
    setUploadProgress(0)

    try {
      // 1. Get presigned upload URL
      const { uploadId, r2Key, uploadUrl } = await uploadsApi.presignUpload(
        file.name,
        file.type
      )
      setUploadProgress(10)

      // 2. Upload file directly to R2
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 80) + 10
            setUploadProgress(pct)
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Upload failed: ${xhr.status}`))
        }
        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
      })
      setUploadProgress(90)

      // 3. Complete the upload and create job
      const upload = await uploadsApi.completeUpload(uploadId, r2Key)
      const job = await createJob({
        uploadId: upload.id,
        outputs: selectedOutputs,
        title: meta.title || undefined,
        description: meta.description || undefined,
        division: meta.division || undefined,
      })
      setUploadProgress(100)

      // 4. Redirect to job detail
      navigate(`/jobs/${job.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.'
      setUploadError(message)
      setIsUploading(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="page-title">New Upload</h1>
      </div>

      <div className="max-w-2xl">
        <Stepper current={step} />

        <div className="card p-6 min-h-80">
          {step === 0 && (
            <FileStep file={file} onFile={handleFileChange} error={fileError} />
          )}
          {step === 1 && (
            <OutputsStep selected={selectedOutputs} onChange={setSelectedOutputs} />
          )}
          {step === 2 && <DetailsStep meta={meta} onChange={setMeta} />}
          {step === 3 && file && (
            <ConfirmStep
              file={file}
              outputs={selectedOutputs}
              meta={meta}
              uploadProgress={uploadProgress}
              isUploading={isUploading}
              uploadError={uploadError}
            />
          )}
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={handleBack}
            disabled={step === 0 || isUploading}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <ChevronLeft size={15} />
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!canAdvance()}
              className="btn-primary flex items-center gap-2"
            >
              Next
              <ChevronRight size={15} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isUploading || !file || selectedOutputs.length === 0}
              className="btn-primary flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  Start Processing
                  <ChevronRight size={15} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
